import type { PlanRunStatusName } from '@steado/admin-contract/plan-runs';

import type { ToneName } from '@/components/table/tone';

/**
 * The badge tone for a run's outcome, in one place — the table and the drawer
 * both draw it, and the same outcome wearing two colours reads as two things.
 *
 * The split is by whose problem it is. Amber is the model's arithmetic or its
 * answer: something a person should read. Red is infrastructure: a provider
 * outage or an unexpected throw, which nobody fixes by looking at the plan.
 * `Record<PlanRunStatusName, …>` makes a new outcome a compile error rather
 * than a silently grey badge.
 */
const RUN_TONES: Readonly<Record<PlanRunStatusName, ToneName>> = {
  running: 'info',
  succeeded: 'ok',
  // The user has a plan, so this is not a failure and must not read as one.
  // Amber rather than green all the same: the numbers were approximated, and
  // a screen full of these is the catalog telling us something.
  succeeded_with_residual: 'warn',
  validation_failed: 'warn',
  model_error: 'warn',
  provider_error: 'err',
  failed: 'err',
  // Nobody's problem to fix — the run worked and a newer request took the week.
  // Grey rather than amber or red: it is spend to notice in aggregate, not an
  // incident to open.
  obsolete: 'mute',
};

export function planRunTone(status: PlanRunStatusName): ToneName {
  return RUN_TONES[status];
}

/**
 * How long until a scheduled time.
 *
 * `formatRelative` in the detail screen's helpers reads a timestamp as "how
 * long ago" and answers "just now" for anything in the future — correct for
 * everything it was written for, and wrong for the only forward-looking value
 * in this console. A cron's next run is always ahead, so it gets its own
 * function rather than a second meaning bolted onto that one.
 *
 * A time that has passed is reported as overdue: for a weekly schedule that is
 * the difference between "fires tonight" and "should have fired and did not".
 */
export function formatCountdown(iso: string, now: Date = new Date()): string {
  const ms = new Date(iso).getTime() - now.getTime();
  if (ms <= 0) return 'overdue';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'in under a minute';
  if (minutes < 60) return `in ${String(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${String(hours)}h`;

  return `in ${String(Math.floor(hours / 24))}d`;
}
